import { cleanTextForComparison, tokenizeComparisonWords } from './comparisonTextCleaner'

// Lightweight token diff utilities for inline overlays

export function tokenizeWords(text: string): string[] {
  return tokenizeComparisonWords(text)
}

export function tokenizeWithSpaces(text: string): Array<{ text: string; isSpace: boolean }> {
  if (!text) return []
  const parts = text.split(/(\s+)/)
  return parts.filter(p => p.length > 0).map(p => ({ text: p, isSpace: /^\s+$/.test(p) }))
}

export function lcsMatrix(a: string[], b: string[]): number[][] {
  const m = a.length, n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }
  return dp
}

export function markUnchangedTokens(pivotTokens: string[], witnessTokens: string[]): boolean[] {
  const dp = lcsMatrix(pivotTokens, witnessTokens)
  let i = pivotTokens.length, j = witnessTokens.length
  const unchanged: boolean[] = Array(witnessTokens.length).fill(false)
  while (i > 0 && j > 0) {
    if (pivotTokens[i - 1] === witnessTokens[j - 1]) {
      unchanged[j - 1] = true
      i--; j--
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--
    } else {
      j--
    }
  }
  return unchanged
}

// Simple character-level Levenshtein distance for small strings
export function charDistance(a: string, b: string): number {
  const m = a.length, n = b.length
  if (m === 0) return n
  if (n === 0) return m
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      )
    }
  }
  return dp[m][n]
}

// Build overlay tokens preserving spaces while marking word tokens as changed/unchanged
export function buildInlineOverlayTokens(pivot: string, witness: string): Array<{ text: string; isSpace: boolean; changed: boolean }> {
  const cleanedWitness = cleanTextForComparison(witness)
  const cleanedPivot = cleanTextForComparison(pivot)
  const wordTokens = tokenizeWords(cleanedWitness)
  const pivotTokens = tokenizeWords(cleanedPivot)
  const keep = markUnchangedTokens(pivotTokens, wordTokens)
  const withSpaces = tokenizeWithSpaces(cleanedWitness)

  let wordIndex = 0
  return withSpaces.map(tok => {
    if (tok.isSpace) return { ...tok, changed: false }
    const changed = !(keep[wordIndex] ?? false)
    const out = { text: tok.text, isSpace: false, changed }
    wordIndex++
    return out
  })
}

// ---------------------------------------------------------------------------
// Information-rich diff: word-level edit script (insert/delete/substitute/match)
// ---------------------------------------------------------------------------

export type EditOp = 'match' | 'insert' | 'delete' | 'substitute'

export interface EditStep {
  op: EditOp
  pivotToken?: string
  witnessToken?: string
}

export function computeWordEditScript(pivotTokens: string[], witnessTokens: string[]): EditStep[] {
  const m = pivotTokens.length
  const n = witnessTokens.length

  // Fast path: identical token sequences need no DP matrix.
  if (m === n) {
    let identical = true
    for (let index = 0; index < m; index += 1) {
      if (pivotTokens[index] !== witnessTokens[index]) {
        identical = false
        break
      }
    }
    if (identical) {
      return pivotTokens.map((token, index) => ({
        op: 'match' as const,
        pivotToken: token,
        witnessToken: witnessTokens[index]
      }))
    }
  }

  // dp[i][j] = cost to transform pivot[0..i) -> witness[0..j), stored as flat
  // typed arrays: one allocation each instead of (m+1) heap rows.
  const width = n + 1
  const dp = new Int32Array((m + 1) * width)
  // bt moves: 0 none, 1 del, 2 ins, 3 sub, 4 mat
  const bt = new Uint8Array((m + 1) * width)

  for (let i = 0; i <= m; i++) dp[i * width] = i
  for (let j = 0; j <= n; j++) dp[j] = j

  for (let i = 1; i <= m; i++) {
    const row = i * width
    const prevRow = row - width
    const pivotToken = pivotTokens[i - 1]
    for (let j = 1; j <= n; j++) {
      const equal = pivotToken === witnessTokens[j - 1]
      const subCost = dp[prevRow + j - 1] + (equal ? 0 : 1)
      const delCost = dp[prevRow + j] + 1
      const insCost = dp[row + j - 1] + 1
      // Tie-break order matches the previous implementation: sub, then del.
      let best = subCost
      let move = equal ? 4 : 3
      if (delCost < best) { best = delCost; move = 1 }
      if (insCost < best) { best = insCost; move = 2 }
      dp[row + j] = best
      bt[row + j] = move
    }
  }

  // Backtrace
  const steps: EditStep[] = []
  let i = m, j = n
  while (i > 0 || j > 0) {
    const move = bt[i * width + j]
    if (move === 4 || move === 3) {
      steps.push({ op: move === 4 ? 'match' : 'substitute', pivotToken: pivotTokens[i - 1], witnessToken: witnessTokens[j - 1] })
      i--; j--
    } else if (move === 1) {
      steps.push({ op: 'delete', pivotToken: pivotTokens[i - 1] })
      i--
    } else if (move === 2) {
      steps.push({ op: 'insert', witnessToken: witnessTokens[j - 1] })
      j--
    } else {
      // When at edges and bt is 'none'
      if (i > 0) { steps.push({ op: 'delete', pivotToken: pivotTokens[i - 1] }); i-- }
      else if (j > 0) { steps.push({ op: 'insert', witnessToken: witnessTokens[j - 1] }); j-- }
    }
  }
  steps.reverse()
  return steps
}

export interface RichOverlayToken {
  kind: 'word' | 'space' | 'ghost'
  text: string
  op?: EditOp // for kind=word or kind=ghost
  morph?: 'near'
  substitutionStrength?: 'mild' | 'strong'
}

type DiffDetailSegment = {
  text: string
  changed: boolean
}

const getGraphemeSegments = (text: string): string[] => {
  if (!text) {
    return []
  }
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    return Array.from(segmenter.segment(text), segment => segment.segment)
  }
  return Array.from(text.normalize('NFC'))
}

const buildDiffDetailSegments = (base: string, compare: string): DiffDetailSegment[] => {
  const baseSegments = getGraphemeSegments(base)
  const compareSegments = getGraphemeSegments(compare)
  const unchanged = markUnchangedTokens(baseSegments, compareSegments)
  const segments: DiffDetailSegment[] = []
  compareSegments.forEach((segment, index) => {
    const changed = !(unchanged[index] ?? false)
    const previous = segments[segments.length - 1]
    if (previous && previous.changed === changed) {
      previous.text += segment
      return
    }
    segments.push({
      text: segment,
      changed
    })
  })
  return segments
}

const getSubstitutionStrength = (pivot: string, witness: string): 'mild' | 'strong' => {
  const segments = buildDiffDetailSegments(pivot, witness)
  const totalGraphemes = segments.reduce((sum, segment) => sum + getGraphemeSegments(segment.text).length, 0)
  const changedGraphemes = segments.reduce(
    (sum, segment) => sum + (segment.changed ? getGraphemeSegments(segment.text).length : 0),
    0
  )
  if (totalGraphemes === 0) {
    return 'mild'
  }
  const changedRatio = changedGraphemes / totalGraphemes
  return changedGraphemes >= 2 || changedRatio >= 0.34 ? 'strong' : 'mild'
}

export function buildRichInlineOverlay(
  pivot: string,
  witness: string,
  options?: {
    classifyNear?: (pivot: string, witness: string) => boolean
    treatNearAsUnchanged?: boolean
    smallEditThreshold?: number
    includeDeletionGhosts?: boolean
  }
): { tokens: RichOverlayToken[]; stats: { total: number; changed: number; inserts: number; deletes: number; substitutes: number } } {
  const cleanedPivot = cleanTextForComparison(pivot)
  const cleanedWitness = cleanTextForComparison(witness)
  const pivotWords = tokenizeWords(cleanedPivot)
  const witnessWords = tokenizeWords(cleanedWitness)
  const steps = computeWordEditScript(pivotWords, witnessWords)
  const withSpaces = tokenizeWithSpaces(cleanedWitness)

  const witnessWordQueue: Array<{ op: EditOp; word: string; pivot?: string }> = []
  const deletionsByWordIndex = new Map<number, string[]>()
  let inserts = 0, deletes = 0, substitutes = 0, changed = 0
  let witnessWordIndex = 0

  for (const s of steps) {
    if (s.op === 'delete') {
      deletes++
      changed++
      if (options?.includeDeletionGhosts) {
        const deletedAtIndex = witnessWordIndex
        const existing = deletionsByWordIndex.get(deletedAtIndex) || []
        existing.push(s.pivotToken || '')
        deletionsByWordIndex.set(deletedAtIndex, existing)
      }
    } else if (s.op === 'insert') {
      inserts++
      changed++
      witnessWordQueue.push({ op: 'insert', word: s.witnessToken || '' })
      witnessWordIndex++
    } else if (s.op === 'substitute') {
      substitutes++
      changed++
      witnessWordQueue.push({ op: 'substitute', word: s.witnessToken || '', pivot: s.pivotToken })
      witnessWordIndex++
    } else {
      // match
      witnessWordQueue.push({ op: 'match', word: s.witnessToken || '', pivot: s.pivotToken })
      witnessWordIndex++
    }
  }

  // Now interleave with spaces
  const tokens: RichOverlayToken[] = []
  let wordIdx = 0
  const pushDeletionGhosts = (index: number) => {
    const ghosts = deletionsByWordIndex.get(index) || []
    ghosts.forEach((ghostText, ghostIndex) => {
      if (!ghostText.trim()) {
        return
      }
      if (tokens.length > 0 && tokens[tokens.length - 1]?.kind !== 'space') {
        tokens.push({ kind: 'space', text: ' ' })
      }
      tokens.push({
        kind: 'ghost',
        text: ghostText,
        op: 'delete'
      })
      if (ghostIndex < ghosts.length - 1 || index < witnessWords.length) {
        tokens.push({ kind: 'space', text: ' ' })
      }
    })
  }
  for (const part of withSpaces) {
    if (part.isSpace) {
      tokens.push({ kind: 'space', text: part.text })
    } else {
      pushDeletionGhosts(wordIdx)
      // Assign next queued op to this witness word
      const entry = witnessWordQueue[wordIdx] || { op: 'match', word: part.text }
      let op: EditOp = entry.op
      let morph: 'near' | undefined = undefined
      if (op === 'substitute' && entry.pivot) {
        let near = false
        if (options?.classifyNear && options.classifyNear(entry.pivot, entry.word)) {
          near = true
        } else if (options?.smallEditThreshold !== undefined) {
          const dist = charDistance(entry.pivot, entry.word)
          if (dist <= options.smallEditThreshold) near = true
        }
        if (near) {
          morph = 'near'
          if (options?.treatNearAsUnchanged) {
            op = 'match'
          }
        }
      }
      tokens.push({
        kind: 'word',
        text: part.text,
        op,
        morph,
        substitutionStrength: op === 'substitute' && entry.pivot && morph !== 'near'
          ? getSubstitutionStrength(entry.pivot, part.text)
          : undefined
      })
      wordIdx++
    }
  }
  pushDeletionGhosts(wordIdx)

  const total = witnessWords.length
  return { tokens, stats: { total, changed, inserts, deletes, substitutes } }
}
